/**
 * Small labelling primitives shared by the Etusivu cards: the blue project
 * tag every task row carries, the gray hankintapaketti tag, and the status
 * pill. Class strings are literal so Tailwind's JIT picks them up.
 */

import { TaskStatus } from "@/types";
import { TASK_STATUS_LABELS_SINGULAR } from "@/lib/taskStatusMeta";
import { cn } from "@/lib/utils";

const PILL_TINT: Record<TaskStatus, string> = {
  [TaskStatus.PENDING]: "bg-muted text-muted-foreground",
  [TaskStatus.IN_PROGRESS]: "bg-yellow-50 text-yellow-700",
  [TaskStatus.COMPLETED]: "bg-green-50 text-green-700",
  [TaskStatus.REJECTED]: "bg-red-50 text-red-700",
  [TaskStatus.CANNOT_MEASURE]: "bg-muted text-muted-foreground",
  [TaskStatus.NOT_APPLICABLE]: "bg-muted text-muted-foreground",
  [TaskStatus.PROBLEM]: "bg-muted text-muted-foreground",
  [TaskStatus.UNKNOWN]: "bg-muted text-muted-foreground",
};

export function ProjectTag({ name }: { name: string }) {
  return (
    <span
      className="max-w-[16rem] truncate rounded border border-border bg-card px-2 py-0.5 text-xs leading-5 text-foreground"
      title={name}
    >
      {name}
    </span>
  );
}

export function PackageTag({
  code,
  name,
}: {
  code?: string | null;
  name?: string | null;
}) {
  const text = code ?? name;
  if (!text) return null;
  return (
    <span
      className="max-w-[14rem] truncate rounded border border-border bg-card px-2 py-0.5 font-mono text-xs leading-5 text-foreground"
      title={[code, name].filter(Boolean).join(" ")}
    >
      {text}
    </span>
  );
}

export function StatusPill({ status }: { status: TaskStatus }) {
  return (
    <span
      className={cn(
        "shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium leading-5",
        PILL_TINT[status] ?? PILL_TINT[TaskStatus.PENDING],
      )}
    >
      {TASK_STATUS_LABELS_SINGULAR[status] ?? status}
    </span>
  );
}
